/**
 * The 4:3 stage. Everything inside is laid out in Unity's reference
 * coordinates (2048×1536) and the whole stage is scaled with a CSS transform to
 * fit the viewport, so activity code never has to think about screen size.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { DropZonesProvider, StageScaleContext } from "./drag";
import { FxLayer } from "./fx";

export const STAGE_W = 2048;
export const STAGE_H = 1536;

export function Stage({ children, background }: { children: ReactNode; background?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const { width, height } = el.getBoundingClientRect();
      setScale(Math.min(width / STAGE_W, height / STAGE_H));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={ref} className="stage-viewport">
      <StageScaleContext.Provider value={scale}>
        <DropZonesProvider>
          <div
            className="stage"
            style={{ width: STAGE_W, height: STAGE_H, transform: `translate(-50%, -50%) scale(${scale})` }}
          >
            {background}
            <FxLayer>{children}</FxLayer>
          </div>
        </DropZonesProvider>
      </StageScaleContext.Provider>
    </div>
  );
}

/** CSS placement for a schema `Layout` (top-left anchored, y negative downwards). */
export function layoutStyle(l: { x: number; y: number; width: number; height: number; rotation: { z: number } }) {
  return {
    position: "absolute" as const,
    left: l.x,
    top: -l.y,
    width: l.width,
    height: l.height,
    transform: l.rotation.z ? `rotate(${-l.rotation.z}deg)` : undefined,
    transformOrigin: "center",
  };
}
